import React, { useState, useEffect } from 'react';
import { 
  Box, TextField, Button, Typography, GlobalStyles, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Divider, Alert, Snackbar 
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import StjgLogo from './img/logo-stjg.png';
import { useNavigate, useLocation } from 'react-router-dom';
import { saveAuthData, clearAuthData, isAuthenticated } from '../../utils/auth';
import api from '../../utils/api';

function Login() {
  const [open, setOpen] = useState(false); // State for modal visibility
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  const [isLogout, setIsLogout] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check URL parameters for logout or session expiry
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const logout = params.get('logout');
    const sessionExpired = params.get('session_expired');
    
    if (logout === 'true') {
      // Clear auth data and show logout message
      clearAuthData();
      setIsLogout(true);
      showMessage('Vous avez été déconnecté avec succès');
      
      // Clean up the URL
      navigate('/', { replace: true });
    } else if (sessionExpired === 'true') {
      // Handle expired session
      showMessage('Votre session a expiré. Veuillez vous reconnecter.', 'warning');
      
      // Clean up the URL
      navigate('/', { replace: true });
    } else if (isAuthenticated()) {
      // If already authenticated and not logging out, redirect to pre-reservation
      navigate('/pre-reservation', { replace: true });
    }
  }, [location, navigate]);
  
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  
  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };
  
  const showMessage = (message, severity = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Use the API utility instead of direct axios call
      const response = await api.post('/login', {
        email,
        password
      });
      
      // Store token and user data
      saveAuthData(response.data.token, response.data.user);
      
      showMessage('Connexion réussie');
      
      // Navigate after successful login
      setTimeout(() => {
        navigate('/pre-reservation');
      }, 1000);
      
    } catch (err) {
      console.error('Login error:', err);
      if (err.response && err.response.data && err.response.data.message) {
        setError(err.response.data.message);
        showMessage(err.response.data.message, 'error');
      } else {
        setError('Une erreur est survenue lors de la connexion');
        showMessage('Une erreur est survenue lors de la connexion', 'error');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetPassword = async () => {
    if (!resetEmail) {
      showMessage('Veuillez entrer votre email', 'error');
      return;
    }
    
    try {
      // This would be your actual password reset API endpoint
      // await api.post('/reset-password', { email: resetEmail });
      
      showMessage('Instructions de réinitialisation envoyées à votre email');
      handleClose();
    } catch (err) {
      showMessage('Erreur lors de l\'envoi des instructions de réinitialisation', 'error');
    }
  }
  return (
    <>
      <GlobalStyles
        styles={{
          '*': { margin: 0, padding: 0, boxSizing: 'border-box' },
          html: { height: '100%' },
          body: { height: '100%', backgroundColor: '#fff' },
          '#root': { height: '100%' },
        }}
      />
      
      {/* Main Login Box */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            width: '100%', 
            height: '100vh',
            overflow: 'hidden',
            fontFamily: 'Littera text',
          }}
        >
          <Box sx={{ width: '65%', backgroundColor: '#000', display: 'flex', justifyContent: 'center', alignItems: 'center' }} />
          
          <Box sx={{ width: '35%', padding: '30px', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ width: '80%' }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <img src={StjgLogo} alt="Global Health Logo" style={{ width: '100px' }} />

                <Typography variant="body1" sx={{ marginBottom: '20px', fontSize: '25px', textAlign: 'center', fontWeight: 'lighter' }}>
                  Connexion
                </Typography>
              </Box>

              <Typography variant="subtitle2" sx={{ fontSize: '17px' }}>Email ou Matricule</Typography>
              <TextField 
                fullWidth 
                placeholder="Bob@exemple.com" 
                variant="outlined" 
                margin="dense"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#000' }, '&.Mui-focused fieldset': { borderColor: 'red' } } }} 
              />

              <Typography variant="subtitle2" sx={{ mt: 1, fontSize: '17px' }}>Password</Typography>
              <TextField 
                fullWidth 
                type="password" 
                placeholder="***********" 
                variant="outlined" 
                margin="dense"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#000' }, '&.Mui-focused fieldset': { borderColor: 'red' } } }} 
              />

              {/* Forgot Password */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Typography
                  variant="body2"
                  sx={{ alignSelf: 'flex-start', color: '#000', cursor: 'pointer', fontSize: '14px', mt: '5px' }}
                  onClick={handleOpen} // Open modal on click
                >
                  Mot de passe oublié ?
                </Typography>

                <Button
                  variant="contained"
                  sx={{
                    backgroundColor: '#000',
                    color: '#fff',
                    borderRadius: '30px',
                    textTransform: 'none',
                    width: '40%',
                    '&:hover': {
                      backgroundColor: 'red',
                    },
                  }}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? 'Connexion...' : 'Se Connecter'}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Reset Password Modal */}
      <Dialog open={open} onClose={handleClose}>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 400, mr: 3  }}>RÉINITIALISER LE MOT DE PASSE</Typography>
          <IconButton onClick={handleClose}><CloseIcon /></IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent>
          <Typography variant="subtitle2" sx={{ fontSize: '17px' }}>Email</Typography>
          <TextField 
            fullWidth 
            placeholder="Votre email" 
            variant="outlined" 
            margin="dense" 
            value={resetEmail}
            onChange={(e) => setResetEmail(e.target.value)}
            sx={{ '& .MuiOutlinedInput-root': { '&:hover fieldset': { borderColor: '#000' }, '&.Mui-focused fieldset': { borderColor: 'red' } } }} 
          />
        </DialogContent>
        <Divider sx={{mb: 1}}/>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button
            variant="outlined"
            sx={{
              color: 'red',
              borderColor: 'red',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
            }}
            startIcon={<CloseIcon />}
            onClick={handleClose}
          >
            Close
          </Button>
          <Button
            variant="outlined"
            sx={{
              color: 'red',
              borderColor: 'red',
              textTransform: 'none',
              '&:hover': { backgroundColor: 'red', color: '#fff', borderColor: 'red' },
            }}
            startIcon={<CheckIcon />}
            onClick={handleResetPassword}
          >
            Envoyer
          </Button>
        </DialogActions>
      </Dialog>
      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbarOpen} 
        autoHideDuration={6000} 
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
}

export default Login;
